import { clsx } from "clsx";
import { motion } from "framer-motion";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useCallback, useState } from "react";

import { Fade } from "../../animations/fade";
import { IconButton } from "../../buttons/icon-button";
import { HorizontalCard } from "../../cards/horizontal-card";
import { PaginationIndicator } from "../../pagination/pagination-indicator";
import { PaginationIndicatorColor } from "../../pagination/pagination-indicator/types";
import { EmphasisSliderSectionProps } from "../../strips/emphasis-slider-section";
import { EmphasisSliderSectionVariants } from "../../strips/emphasis-slider-section/types";

export interface EmphasisSliderProps
  extends Pick<EmphasisSliderSectionProps, "slides" | "variant">,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  isMobile?: boolean;
}

export const EmphasisSlider: FC<EmphasisSliderProps> = ({
  slides,
  className,
  isMobile = false,
  variant,
}) => {
  const isInternalVariant =
    variant === EmphasisSliderSectionVariants.INTERNAL ||
    variant === EmphasisSliderSectionVariants.INTERNAL_LIGHT;

  const isPrimaryVariant =
    variant === EmphasisSliderSectionVariants.INTERNAL ||
    variant === EmphasisSliderSectionVariants.EXTERNAL;

  const [page, setPage] = useState(0);

  const paginate = useCallback(
    (newDirection: number) => {
      setPage(page + newDirection);
    },
    [page]
  );

  const hasPrevious = page > 0;
  const hasNext = page < (slides ?? []).length - 1;

  if (!slides?.length) return null;

  return (
    <div className={clsx("w-full flex flex-col items-center gap-10 relative", className)}>
      <div className="relative w-full overflow-x-hidden">
        <div
          className="px-0 w-full flex items-end h-full transition-all duration-500 ease-out"
          style={{
            transform: isMobile ? `translateX(${-page * 100}%)` : `translateX(${20 + -60 * page}%)`,
          }}
        >
          {(slides ?? [])
            .filter((item) => !!item)
            .map((item, i) => {
              const showBody = i === page || isMobile;
              return (
                <motion.div
                  key={i}
                  className={clsx(
                    "flex-[0_0_100%] self-stretch transition-all duration-500 ease-out",
                    "xl:flex-[0_0_60%] xl:w-[60%]",
                    "px-4"
                  )}
                  transition={{
                    duration: 0.5,
                    ease: "easeOut",
                  }}
                  {...(isMobile && {
                    drag: "x",
                    dragConstraints: { left: 0, right: 0 },
                    dragElastic: 0,
                    onDragEnd: (_, { offset }) => {
                      if (offset.x < 0 && hasNext) {
                        paginate(1);
                      } else if (offset.x > 0 && hasPrevious) {
                        paginate(-1);
                      }
                    },
                    dragMomentum: false,
                  })}
                >
                  {/* Height management requires us to use this wrapping div to correctly handle shadow */}
                  <div className="pb-4 px-4 xl:px-0 w-full h-full flex items-end xl:h-[60vh]">
                    <HorizontalCard
                      {...(isInternalVariant
                        ? { ...item! }
                        : { ...item!, title: null, ctas: null, description: null })}
                      showBody={showBody}
                      showHighlights={false}
                      showMultipleImages={!isMobile}
                      className={clsx("transition-all w-full", showBody ? "h-full" : "h-[200px]")}
                    />
                  </div>
                </motion.div>
              );
            })}
        </div>

        <Fade visible={hasPrevious}>
          <IconButton
            className="absolute left-2 xl:left-8 bottom-60 hidden xl:flex"
            icon="arrow-left"
            variant="light"
            onClick={() => paginate(-1)}
          />
        </Fade>
        <Fade visible={hasNext}>
          <IconButton
            className="absolute right-2 xl:right-8 bottom-60 hidden xl:flex"
            icon="arrow-right"
            variant="light"
            onClick={() => paginate(1)}
          />
        </Fade>
      </div>
      {!isInternalVariant && (
        <div className="text-2xl font-normal text-center text-light min-h-8">
          <Fade duration={0.4} key={page}>
            {slides[page]?.title}
          </Fade>
        </div>
      )}
      <PaginationIndicator
        activeItem={page}
        color={isPrimaryVariant ? PaginationIndicatorColor.Light : PaginationIndicatorColor.Dark}
        totalItems={slides.length}
        onClickItem={(i) => setPage(i)}
        className="px-8 lg:px-20 z-10"
      />
    </div>
  );
};
